import { useEffect, useRef, useState, type ReactNode } from 'react';
import { motion } from 'motion/react';
import ImagePlaceholder from './ui/ImagePlaceholder';
import PhotoCaptionOverlay from './ui/PhotoCaptionOverlay';
import InlineText from './ui/InlineText';
import AiPhotoBadge from './ui/AiPhotoBadge';
import Button from './ui/Button';
import { cn } from '../lib/cn';
import { useMediaSlot } from '../lib/MediaContext';
import { resolveMediaUrl } from '../lib/mediaApi';
import { useBookingModal } from './booking/BookingModalContext';
import { heroSlideImageSlotKey, type HeroSlide } from '../lib/heroSlidesApi';

const BOOK_NOW_LINK = '#book-now';

// Split from MediaImage (rather than using it directly) because the Ken
// Burns zoom below wraps only this image — MediaImage bundles the photo's
// caption overlay in the same element, which would zoom right along with
// it. Pulling the raw <img> out lets the caption render as a sibling,
// outside the scaling wrapper, so the photo zooms but its caption text
// doesn't.
function HeroSlideImage({ slide, priority }: { slide: HeroSlide; priority: boolean }) {
  const slot = useMediaSlot(heroSlideImageSlotKey(slide.id));
  const label = `Hero slide — ${slide.title}`;
  if (!slot?.file_path) {
    return <ImagePlaceholder label={label} height="100%" rounded={false} className="h-full" />;
  }
  return (
    <img
      src={resolveMediaUrl(slot.file_path)}
      alt={slide.title}
      loading={priority ? 'eager' : 'lazy'}
      fetchPriority={priority ? 'high' : 'auto'}
      decoding={priority ? 'sync' : 'async'}
      className="w-full h-full object-cover"
    />
  );
}

function HeroSlideCaption({ slide }: { slide: HeroSlide }) {
  const slot = useMediaSlot(heroSlideImageSlotKey(slide.id));
  if (!slot?.file_path) return null;
  return (
    <>
      <PhotoCaptionOverlay text={slot.caption} />
      {!!slot.ai_enhanced && <AiPhotoBadge />}
    </>
  );
}

function HeroButton({ text, link, variant }: { text: string; link: string; variant: 'olive' | 'outline-light' }) {
  const { openBooking } = useBookingModal();
  if (!text) return null;

  if (link === BOOK_NOW_LINK) {
    return (
      <Button variant={variant} size="lg" onClick={() => openBooking()} pulse={variant === 'olive'}>
        {text}
      </Button>
    );
  }
  if (/^https?:\/\//.test(link)) {
    return (
      <Button variant={variant} size="lg" href={link}>
        {text}
      </Button>
    );
  }
  return (
    <Button variant={variant} size="lg" to={link || '/'}>
      {text}
    </Button>
  );
}

// Orchestrates the title/subtitle/button entrance: children stagger in only
// while their slide is the active one, and reset to "inactive" the instant
// it isn't — so the animation replays fresh every time a slide comes back
// around, instead of only ever playing once on mount.
const contentContainerVariants = {
  inactive: { transition: { staggerChildren: 0 } },
  active: { transition: { staggerChildren: 0.1, delayChildren: 0.25 } },
};

const contentItemVariants = {
  inactive: { opacity: 0, y: 26 },
  active: { opacity: 1, y: 0, transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] as const } },
};

function SlideContent({ slide, badges, active }: { slide: HeroSlide; badges?: ReactNode; active: boolean }) {
  return (
    <motion.div
      variants={contentContainerVariants}
      initial="inactive"
      animate={active ? 'active' : 'inactive'}
      className="absolute inset-0 z-[2] max-w-page mx-auto px-8 flex flex-col justify-end pb-24 md:pb-35"
    >
      {badges && (
        <motion.div variants={contentItemVariants} className="flex gap-2.5 mb-5 flex-wrap">
          {badges}
        </motion.div>
      )}
      <motion.h1
        variants={contentItemVariants}
        className="font-serif font-medium text-[36px] md:text-[56px] leading-tight text-paper max-w-[760px] m-0 mb-4.5"
      >
        {slide.title}
      </motion.h1>
      <motion.p variants={contentItemVariants} className="text-[18px] leading-relaxed text-[#EDE8DC] max-w-[560px] m-0 mb-8">
        <InlineText text={slide.subtitle} tone="light" />
      </motion.p>
      <motion.div variants={contentItemVariants} className="flex gap-3.5 flex-wrap">
        <HeroButton text={slide.primary_text} link={slide.primary_link} variant="olive" />
        <HeroButton text={slide.secondary_text} link={slide.secondary_link} variant="outline-light" />
      </motion.div>
    </motion.div>
  );
}

export default function HeroSlider({
  slides,
  badges,
  holdSeconds = 7,
}: {
  slides: HeroSlide[];
  badges?: ReactNode;
  holdSeconds?: number;
}) {
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const holdMs = Math.min(30, Math.max(1, holdSeconds)) * 1000;

  // The Ken Burns motion.div for slide 0 doesn't exist in the DOM at all
  // until `slides` has data (this component returns null before then — see
  // below), so gating this effect on `[]` used to schedule the delayed flip
  // the instant HeroSlider first mounted, often well before the slide data
  // (and so the Ken Burns div itself) ever arrived. By the time that div
  // finally did mount, `started` was already true, so it mounted straight
  // into the zoomed state with no painted scale:1 frame to transition from
  // — an instant snap instead of a tween. Re-running this effect once
  // `slides.length` goes from 0 to positive means it fires on the exact
  // render where the Ken Burns divs are first created, so the delayed flip
  // always has a real painted initial frame to animate from.
  //
  // A plain setTimeout, not requestAnimationFrame — a backgrounded/inactive
  // tab (e.g. opened in a background tab, or an inactive browser window)
  // can throttle rAF indefinitely, which would leave the hero permanently
  // un-zoomed. setTimeout still fires regardless of tab visibility.
  const [started, setStarted] = useState(false);
  useEffect(() => {
    if (slides.length === 0) return;
    const timer = setTimeout(() => setStarted(true), 50);
    return () => clearTimeout(timer);
  }, [slides.length > 0]);

  // intervalRef guards against a second timer ever coexisting with the
  // first, no matter why the effect body below ends up running more than
  // once for the same mount (observed to happen even in a production
  // build, so not just a StrictMode dev double-invoke) — without it, two
  // live intervals both advance `index` every tick, which silently eats
  // the very first slide's hold time (it was getting cut to a couple
  // hundred ms instead of the configured duration).
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (slides.length <= 1 || paused) return;
    if (intervalRef.current !== null) return;
    intervalRef.current = setInterval(() => setIndex((i) => (i + 1) % slides.length), holdMs);
    return () => {
      if (intervalRef.current !== null) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [slides.length, holdMs, paused]);

  if (slides.length === 0) return null;
  const safeIndex = index % slides.length;

  return (
    <div
      className="absolute inset-0 overflow-hidden"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocus={() => setPaused(true)}
      onBlur={() => setPaused(false)}
    >
      <motion.div
        className="flex h-full"
        animate={{ x: `-${(safeIndex * 100) / slides.length}%` }}
        transition={{ type: 'spring', stiffness: 260, damping: 32 }}
        style={{ width: `${slides.length * 100}%` }}
      >
        {slides.map((s, i) => {
          const active = i === safeIndex;
          return (
            <div key={s.id} className="relative h-full shrink-0 overflow-hidden" style={{ width: `${100 / slides.length}%` }}>
              {/* Ken Burns: slow continuous zoom while a slide is on screen, resets
                  the instant it isn't so it always restarts from scale 1 next time.
                  `started` (see above) is what makes the very first slide's zoom
                  actually animate instead of appearing pre-zoomed. */}
              <motion.div
                className="absolute inset-0"
                initial={{ scale: 1 }}
                animate={{ scale: active && started ? 1.08 : 1 }}
                transition={{ duration: active ? holdMs / 1000 : 0.6, ease: active ? 'linear' : 'easeOut' }}
              >
                <HeroSlideImage slide={s} priority={i === 0} />
              </motion.div>
              <HeroSlideCaption slide={s} />
              <div
                className="absolute inset-0 pointer-events-none"
                style={{
                  background:
                    'linear-gradient(90deg, rgba(33,32,28,0.55) 0%, rgba(33,32,28,0.22) 45%, rgba(33,32,28,0) 75%), ' +
                    'linear-gradient(0deg, rgba(33,32,28,0.72) 0%, rgba(33,32,28,0.15) 55%, rgba(33,32,28,0.35) 100%)',
                }}
              />
              <SlideContent slide={s} badges={badges} active={active} />
            </div>
          );
        })}
      </motion.div>

      {slides.length > 1 && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-[3] w-full max-w-[280px] sm:max-w-[360px] px-8">
          {slides.map((s, i) => (
            <button
              key={s.id}
              aria-label={`Go to slide ${i + 1}`}
              onClick={() => setIndex(i)}
              className="group relative h-[3px] flex-1 rounded-full bg-paper/30 overflow-hidden cursor-pointer"
            >
              {i === safeIndex ? (
                <motion.span
                  key={`progress-${s.id}`}
                  className="absolute inset-y-0 left-0 w-full origin-left bg-paper rounded-full"
                  initial={{ scaleX: 0 }}
                  animate={{ scaleX: 1 }}
                  transition={{ duration: holdMs / 1000, ease: 'linear' }}
                />
              ) : (
                <span className={cn('absolute inset-y-0 left-0 rounded-full bg-paper/70', i < safeIndex ? 'w-full' : 'w-0')} />
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
